import type { DateRange, UUID } from '@cobliteam/common-types';

import type { Configuration } from '../../..';
import HttpMethod from '../../../request/HttpMethod';
import Requester from '../../../request/Requester';
import TimeZone from '../../../request/Timezone';
import type { IRequest } from '../../../request/Types';

export class PerformanceReports {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getDistanceDrivenReport = (period: DateRange, vehicleIds?: UUID[]): Promise<Blob> => {
    const tz = TimeZone.getTimezone();

    const requestBody = {
      start_time: period.start,
      end_time: period.end,
      timezone: tz,
      vehicle_ids: vehicleIds,
    };

    const requestData: IRequest = {
      endpoint: '/analytics-report-api/v1/distance-driven/report',
      method: HttpMethod.POST,
      responseType: 'arraybuffer',
      body: JSON.stringify(requestBody),
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data]));
  };

  public getIncidentsReport = (startInMillis: number, endInMillis: number): Promise<Blob> => {
    const tz = TimeZone.getEncodedTimeZone();
    const requestData: IRequest = {
      endpoint: `/stats/incidents/report?begin=${startInMillis}&end=${endInMillis}&tz=${tz}`,
      method: HttpMethod.GET,
      responseType: 'arraybuffer',
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data]));
  };

  public getRoutesPointsReport = (period: DateRange, vehicleId: UUID, sampleInMinutes?: string): Promise<Blob> => {
    const tz = TimeZone.getEncodedTimeZone();
    const periodParams = `begin=${period.start}&end=${period.end}&tz=${tz}`;
    const sampleParam = sampleInMinutes ? `&sample_in_minutes=${sampleInMinutes}` : '';
    const requestData: IRequest = {
      endpoint: `/stats/reports/points/vehicle?${periodParams}&vehicle_id=${vehicleId}${sampleParam}`,
      method: HttpMethod.GET,
      responseType: 'arraybuffer',
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data]));
  };

  public getDriversOutOfJourneyReport = (period: DateRange, workStartMillis: number, workEndMillis: number, driverIds?: UUID[]): Promise<Blob> => {
    const tz = TimeZone.getEncodedTimeZone();
    const workParams = `work_journey_start=${workStartMillis}&work_journey_end=${workEndMillis}`;
    const periodParams = `begin=${period.start}&end=${period.end}&tz=${tz}`;

    const requestData: IRequest = {
      endpoint: `/stats/reports/out-of-work/driver?${periodParams}&${workParams}`,
      method: HttpMethod.POST,
      responseType: 'arraybuffer',
      body: driverIds ? JSON.stringify(driverIds) : undefined,
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data]));
  };

  public getVehiclesOutOfJourneyReport = (period: DateRange, workStartMillis: number, workEndMillis: number, vehicleIds?: UUID[]): Promise<Blob> => {
    const tz = TimeZone.getEncodedTimeZone();
    const workParams = `work_journey_start=${workStartMillis}&work_journey_end=${workEndMillis}`;
    const periodParams = `begin=${period.start}&end=${period.end}&tz=${tz}`;

    const requestData: IRequest = {
      endpoint: `/stats/reports/out-of-work/vehicle?${periodParams}&${workParams}`,
      method: HttpMethod.POST,
      responseType: 'arraybuffer',
      body: vehicleIds ? JSON.stringify(vehicleIds) : undefined,
    };

    return this.requester.request(requestData).then((response) => new Blob([response.data]));
  };
}

export default PerformanceReports;
